"use client";

import { DataTable } from "@/components/tool-kit/primitives";
import {
  StepList,
  type WorkedStep,
} from "@/components/tools/algebra/step-list";
import type { POLYNOMIAL_CALCULATOR_STRINGS } from "@/components/tools/polynomial-calculator";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  columnTable,
  horner,
  longDivision,
  partialProducts,
  type RootsResult,
} from "@/lib/tool-kit/algebra/polynomial";
import {
  formatUni,
  substitutionText,
  type Uni,
  uniAdd,
  uniDegree,
  uniEval,
  uniMul,
  uniNeg,
} from "@/lib/tool-kit/algebra/uni";
import { decimalMarkOf } from "@/lib/tool-kit/math/locale";
import {
  equalsR,
  formatStyled,
  intR,
  type NumberStyle,
  negR,
  type Rational,
} from "@/lib/tool-kit/math/rational";

type Strings = typeof POLYNOMIAL_CALCULATOR_STRINGS;

export interface PolyContext {
  variable: string;
  strings: Strings;
  style: NumberStyle;
  mark: string;
}

const text = (p: Uni, c: PolyContext) =>
  formatUni(p, c.variable, c.style, c.mark);
const bracketed = (p: Uni, c: PolyContext) => `(${text(p, c)})`;

export function AddSubtractSteps({
  p,
  q,
  subtract,
  ctx,
}: {
  p: Uni;
  q: Uni;
  subtract: boolean;
  ctx: PolyContext;
}) {
  const { strings } = ctx;
  const second = subtract ? uniNeg(q) : q;
  const rows = columnTable(p, q, subtract);
  const show = (v: Rational) => formatStyled(v, ctx.style, ctx.mark);
  const steps: WorkedStep[] = [
    {
      text: subtract ? strings.writeDifference : strings.writeSum,
      math: `${bracketed(p, ctx)} ${subtract ? "−" : "+"} ${bracketed(q, ctx)}`,
    },
  ];
  if (subtract) {
    steps.push({
      text: strings.distributeMinus,
      math: `${bracketed(p, ctx)} + ${bracketed(second, ctx)}`,
    });
  }
  steps.push({ text: strings.combineLike, math: text(uniAdd(p, second), ctx) });
  return (
    <>
      <StepList steps={steps} />
      <DataTable
        head={[
          strings.power,
          strings.firstPolynomial,
          subtract ? strings.negatedSecond : strings.secondPolynomial,
          strings.sum,
        ]}
        rows={rows.map((r) => [
          r.degree,
          show(r.p),
          show(subtract ? negR(r.q) : r.q),
          show(r.result),
        ])}
      />
    </>
  );
}

export function MultiplySteps({
  p,
  q,
  ctx,
}: {
  p: Uni;
  q: Uni;
  ctx: PolyContext;
}) {
  const { strings } = ctx;
  const parts = partialProducts(p, q);
  const total = uniMul(p, q);
  const steps: WorkedStep[] = [
    {
      text: strings.multiplyIntro,
      math: `${bracketed(p, ctx)} × ${bracketed(q, ctx)}`,
    },
    ...parts.map((part) => ({
      text: strings.partialProduct,
      math: `${text(part.term, ctx)} × ${bracketed(q, ctx)} = ${text(part.product, ctx)}`,
    })),
    {
      text: strings.addPartials,
      math: `${parts.map((part) => bracketed(part.product, ctx)).join(" + ")} = ${text(total, ctx)}`,
    },
  ];
  return <StepList steps={steps} />;
}

export function DivideSteps({
  p,
  q,
  ctx,
}: {
  p: Uni;
  q: Uni;
  ctx: PolyContext;
}) {
  const { strings } = ctx;
  const result = longDivision(p, q);
  const show = (v: Rational) => formatStyled(v, ctx.style, ctx.mark);
  const steps: WorkedStep[] = [
    {
      text: strings.divideIntro,
      math: `${bracketed(p, ctx)} ÷ ${bracketed(q, ctx)}`,
    },
  ];
  result.rounds.forEach((round, index) => {
    const lead = round.leadingBefore;
    steps.push({
      text: t(strings.divideRound, {
        n: index + 1,
        dividend: text(
          [
            ...Array.from({ length: lead.degree }, () => intR(0)),
            lead.coefficient,
          ],
          ctx
        ),
        divisor: text(
          [
            ...Array.from({ length: uniDegree(q) }, () => intR(0)),
            q[uniDegree(q)],
          ],
          ctx
        ),
        term: text(round.quotientTerm, ctx),
      }),
      math: `${text(round.quotientTerm, ctx)} × ${bracketed(q, ctx)} = ${text(round.product, ctx)}\n${strings.remainderSoFar} ${text(round.remainder, ctx)}`,
    });
  });
  const exact = result.remainder.length === 0;
  steps.push({
    text: exact ? strings.divideExact : strings.divideRemainder,
    math: `${strings.quotient}: ${text(result.quotient, ctx)}\n${strings.remainder}: ${text(result.remainder, ctx)}`,
  });
  const verify = uniAdd(uniMul(q, result.quotient), result.remainder);
  const linear = uniDegree(q) === 1 && equalsR(q[1], intR(1)) ? q[0] : null;
  return (
    <>
      <StepList steps={steps} />
      <p className="font-mono text-foreground text-sm">
        {t(strings.divideCheck, {
          divisor: bracketed(q, ctx),
          quotient: bracketed(result.quotient, ctx),
          remainder: text(result.remainder, ctx),
          total: text(verify, ctx),
        })}
      </p>
      {linear ? (
        <SyntheticTable at={negR(linear)} ctx={ctx} p={p} show={show} />
      ) : null}
    </>
  );
}

export function SyntheticTable({
  p,
  at,
  ctx,
  show,
}: {
  p: Uni;
  at: Rational;
  ctx: PolyContext;
  show: (v: Rational) => string;
}) {
  const { strings } = ctx;
  const table = horner(p, at);
  return (
    <div className="space-y-2">
      <p className="text-muted-foreground text-sm">
        {t(strings.syntheticIntro, { at: show(at), variable: ctx.variable })}
      </p>
      <DataTable
        head={[
          strings.syntheticRow,
          ...table.coefficients.map((_, i) =>
            String(table.coefficients.length - 1 - i)
          ),
        ]}
        rows={[
          [strings.coefficients, ...table.coefficients.map(show)],
          [strings.carried, ...table.products.map((v) => (v ? show(v) : ""))],
          [strings.totals, ...table.sums.map(show)],
        ]}
      />
    </div>
  );
}

export function EvaluateSteps({
  p,
  at,
  ctx,
}: {
  p: Uni;
  at: Rational;
  ctx: PolyContext;
}) {
  const { strings } = ctx;
  const show = (v: Rational) => formatStyled(v, ctx.style, ctx.mark);
  const value = uniEval(p, at);
  return (
    <>
      <StepList
        steps={[
          {
            text: t(strings.substitute, {
              variable: ctx.variable,
              at: show(at),
            }),
            math: `P(${show(at)}) = ${substitutionText(p, at, ctx.style, ctx.mark)} = ${show(value)}`,
          },
          {
            text: t(strings.remainderTheorem, {
              variable: ctx.variable,
              at: show(at),
            }),
          },
        ]}
      />
      <SyntheticTable at={at} ctx={ctx} p={p} show={show} />
    </>
  );
}

export function RootsView({
  roots,
  ctx,
}: {
  roots: RootsResult;
  ctx: PolyContext;
}) {
  const fmt = useToolNumber();
  const { strings, variable } = ctx;
  const num = (value: number) =>
    fmt(value, { maximumFractionDigits: 4 }).replace("-", "−");
  const mark = decimalMarkOf(fmt);
  const lines: string[] = roots.rational.map((root) => {
    const value = formatStyled(root.value, ctx.style, mark);
    const base = `${variable} = ${value}`;
    return root.multiplicity > 1
      ? `${base}  (${t(strings.multiplicity, { n: root.multiplicity })})`
      : base;
  });
  for (const z of roots.numeric) {
    if (z.im === 0) {
      lines.push(`${variable} ≈ ${num(z.re)}`);
    } else if (z.im > 0) {
      lines.push(`${variable} ≈ ${num(z.re)} ± ${num(z.im)}i`);
    }
  }
  if (lines.length === 0) {
    return <p className="text-muted-foreground text-sm">{strings.noRoots}</p>;
  }
  return (
    <div className="space-y-2">
      <pre className="whitespace-pre-wrap font-mono text-foreground text-sm">
        {lines.join("\n")}
      </pre>
      {roots.numeric.length > 0 ? (
        <p className="text-muted-foreground text-xs">{strings.numericNote}</p>
      ) : null}
      {roots.rationalComplete ? null : (
        <p className="text-muted-foreground text-xs">{strings.largeNote}</p>
      )}
    </div>
  );
}
